<template>
  <div>
    <div class="modal__confirm__delete-wrapper text-dark"
         :class="[{'js-no-close': appendToBody}, {'modal__confirm__delete__wider': isShowDontAskAgain}]"
         ref="d_modal"
         @click.self="$emit('close')">
      <div class="modal__confirm__delete" ref="modalContent" tabindex="-1"
           role="dialog" aria-modal="true" :aria-label="ariaLabel || null">
        <div class="close-cross icon__bg-circle" v-if="showClose" @click="$emit('close')"
             role="button" tabindex="0" :aria-label="i18n.cancel"
             @keydown.enter.stop="$emit('close')" @keydown.space.prevent.stop="$emit('close')">
          <span class="icon-cross2" aria-hidden="true"></span>
        </div>
        <slot name="insertComponent">
          <div class="modal__confirm__delete__template" :style="{width: customWidth}" slot="insertComponent">
            <div class="modal__confirm__header">
              <div class="modal__confirm__delete__close icon__bg-circle" @click.stop="$emit('close')"
                   role="button" tabindex="0" :aria-label="i18n.cancel"
                   @keydown.enter.stop="$emit('close')" @keydown.space.prevent.stop="$emit('close')">
                <span class="icon-cross2" aria-hidden="true"></span>
              </div>
              <slot name="item"></slot>
              <template v-if="isShowConfirmDeleteMessage">
                <h4 v-if="!isOnlyOwnCalendarText">{{ i18n.are_you_sure }}</h4>
                <h4 v-if="isOnlyOwnCalendarText">{{ i18n.are_you_sure_own_calendar }}</h4>
              </template>
              <div class="custom-warning">
                <slot name="custom_warning"></slot>
              </div>
              <div class="modal__confirm__delete__buttons" :class="{'modal__confirm__delete__buttons__no-margin': noMarginOnButton}">
                <div class="do-checkbox-group" v-if="isShowDontAskAgain">
                  <input type="checkbox" v-model="isDontAskAgain" id="dontAskAgain" class="form-check-input">
                  <label for="dontAskAgain" class="d-label form-check-label">{{ i18n.dont_ask_me_again }}</label>
                </div>
                <div class="modal__confirm__delete__buttons__buttons">
                  <slot name="confirm_buttons">
                    <button @click.stop="$emit('close')">{{ i18n.cancel }}</button>
                    <button class="bttn bttn--danger"
                            :class="{'bttn--loading': isSubmitButtonLoading}"
                            ref="submit_bttn"
                            @keyup.enter="onConfirm "
                            @keyup.esc="$emit('close')"
                            @click.stop="onConfirm">
                      <slot name="confirmText">{{ i18n.delete }}</slot>
                    </button>
                  </slot>
                </div>
              </div>
            </div>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
const I18N = {
  da: {
    are_you_sure: 'Er du sikker på, at du vil slette denne?',
    are_you_sure_own_calendar: 'Er du sikker på, at du vil slette denne brik? Det vil kun gælde for denne kalender og kan ikke fortrydes. Venligst bekræft, hvis du ønsker at fortsætte.',
    delete: 'Slet',
    cancel: 'Annuller',
    dont_ask_me_again: 'Spørg mig ikke igen'
  },
  nb: {
    are_you_sure: 'Er du sikker på at du vil slette dette?',
    are_you_sure_own_calendar: 'Er du sikker på at du vil slette dette? Dette vil kun gjelde for denne kalenderen og kan ikke reverseres. Vennligst bekreft at du ønsker å fortsette.',
    delete: 'Slett',
    cancel: 'Avbryt',
    dont_ask_me_again: 'Ikke spør meg igjen'
  },
  en: {
    are_you_sure: 'Are you sure that you want to delete this?',
    are_you_sure_own_calendar: 'Are you sure you want to delete this? This will only apply to this calendar and cannot be undone. Please confirm if you wish to proceed.',
    delete: 'Delete',
    cancel: 'Cancel',
    dont_ask_me_again: 'Don\'t ask me again'
  }
}

// Delt stak over åbne bekræftelses-modaler, så Esc kun lukker den øverste.
const confirmModalStack = []

export default {
  props: {
    showClose: {
      type: Boolean,
      default: true
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: ''
    },
    isOnlyOwnCalendarText: {
      type: Boolean,
      default: false
    },
    isSubmitButtonLoading: {
      type: Boolean,
      default: false
    },
    isShowDontAskAgain: {
      type: Boolean,
      default: false
    },
    propLocale: {
      default: null
    },
    isShowConfirmDeleteMessage: {
      type: Boolean,
      default: true
    },
    customWidth: {
      default: '350px'
    },
    noMarginOnButton: {
      default: false
    }
  },
  data () {
    return {
      isDontAskAgain: false
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return !this.propLocale ? I18N[this.locale] : I18N[this.propLocale]
    }
  },
  methods: {
    onConfirm () {
      this.$emit('confirm', this.isDontAskAgain)
    },
    getFocusable () {
      const root = this.$refs.modalContent
      if (!root) return []
      const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      return Array.from(root.querySelectorAll(selector)).filter(el => {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      })
    },
    handleKeydown (event) {
      if (confirmModalStack[confirmModalStack.length - 1] !== this) return
      if (event.key === 'Escape') {
        this.$emit('close')
        return
      }
      // Fokus-trap (WCAG 2.4.3).
      if (event.key === 'Tab') {
        const root = this.$refs.modalContent
        if (!root) return
        const focusable = this.getFocusable()
        if (focusable.length === 0) {
          event.preventDefault()
          root.focus()
          return
        }
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (!root.contains(document.activeElement)) {
          event.preventDefault()
          first.focus()
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
  },
  mounted () {
    confirmModalStack.push(this)
    window.addEventListener('keydown', this.handleKeydown)
    // Bevarer eksisterende adfærd: fokusér bekræft-knappen ved åbning.
    this.$nextTick(_ => {
      const el = this.$refs.submit_bttn
      if (el) {
        this.$refs.submit_bttn.focus()
      } else {
        const focusable = this.getFocusable()
        if (focusable.length > 0) {
          focusable[0].focus()
        } else if (this.$refs.modalContent) {
          this.$refs.modalContent.focus()
        }
      }
    })
    if (this.appendToBody) {
      const el = this.$refs.d_modal
      const bod = document.getElementById('djs-content-container')
      if (bod) {
        bod.appendChild(el)
      }
    }
  },
  beforeDestroy () {
    const idx = confirmModalStack.indexOf(this)
    if (idx > -1) {
      confirmModalStack.splice(idx, 1)
    }
    window.removeEventListener('keydown', this.handleKeydown)
    const el = this.$refs.d_modal
    if (el) {
      el.remove()
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.modal__confirm__delete-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1212121212;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal__confirm__delete {
  position: relative;
  z-index: 9999;
  background: #f6f9fc;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .33);
  animation-name: fadeUp;
  animation-duration: 0.25s;
}

.modal__confirm__delete__close {
  cursor: pointer;
  color: #ffffff;
}

.close-cross {
  position: absolute;
  color: #ffffff;
  top: -1.5rem;
  right: 0rem;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
}

.modal__confirm__delete {
  position: relative;
  display: flex;
  flex-direction: column;
}

.modal__confirm__delete__close {
  position: absolute;
  top: 0;
  right: 0;
  margin: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--grey);
  transition: 0.25s;
}

.modal__confirm__delete__close:hover {
  color: var(--dark-blue);
}

.close-cross:focus-visible,
.modal__confirm__delete__close:focus-visible,
.modal__confirm__delete:focus-visible {
  outline: 2px solid #468AD7;
  outline-offset: 2px;
}

.modal__confirm__delete h4 {
  margin: 0;
  padding: 2rem 1rem;
}

.modal__confirm__delete h3 { 
  margin: 0;
  padding: 2rem 1rem 0 1rem;
}

.modal__confirm__delete__buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 1rem;
}

.modal__confirm__delete__buttons.modal__confirm__delete__buttons__no-margin {
  margin-top: 0;
}

.modal__confirm__delete__wider .modal__confirm__delete__buttons {
  justify-content: space-between;
}

.modal__confirm__delete__buttons button:last-child {
  margin-left: 0.5rem;
}

.modal__confirm__delete__template {
  width: 350px;
}

.modal__confirm__delete__wider .modal__confirm__delete__template {
  width: 400px;
}

.bttn--danger {
  color: var(--danger);
  background-color: #ffffff;
}

.d-modal--from-top {
  align-items: flex-start;
  padding-top: 150px;
}

.modal__confirm__delete__buttons {
  padding: 1rem;
  background-color: #ffffff;
  border-top: 1px solid var(--ligthGrey);
}

.do-checkbox-group {
  display: flex;
  align-items: center;
  margin-right: 1rem;
  gap: 0.5rem;
  cursor: pointer;
}

.do-checkbox-group label {
  cursor: pointer;
  text-transform: initial;
}

.modal__confirm__delete__buttons__buttons {
  display: flex;
  align-items: center;
}

</style>



// WEBPACK FOOTER //
// ModalConfirmation.vue?8bc0aee6